import React from 'react'
import _ from 'lodash'

import { useFormContext, useController } from 'react-hook-form'

import { Input } from 'modules/ui/atoms/Input'
import { mergeRefs } from '../utils'

export type InputFieldProps = {
  id: string | number
  label: string
  type?: string
  caption?: string
  captionId?: string
  error?: { message: string }
  maxLength?: number
  required?: boolean
  placeholder?: string
  hideErrorCaption?: boolean
  hasOverflow?: boolean
  hasCounter?: boolean
  overwriteValueWithFocus?: boolean
  autoComplete?: string
  inputMode?:
    | 'text'
    | 'search'
    | 'none'
    | 'email'
    | 'tel'
    | 'url'
    | 'numeric'
    | 'decimal'
    | undefined
  inputRef?: React.RefObject<HTMLElement>
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onFocus?: () => void
  onBlur?: () => void
  dataId?: string
  autoFocus?: boolean
}

const InputField: React.FC<InputFieldProps> = ({
  id,
  label,
  error,
  required,
  caption,
  captionId,
  type,
  maxLength,
  placeholder,
  hideErrorCaption,
  inputMode,
  inputRef,
  onKeyDown,
  hasCounter = true,
  hasOverflow = true,
  overwriteValueWithFocus = false,
  autoComplete,
  onFocus,
  onBlur,
  dataId = '',
  autoFocus,
}) => {
  const { control } = useFormContext()

  const { field, fieldState, formState } = useController({
    name: `${id}`,
    control,
  })

  const { isDirty } = fieldState
  const { isSubmitted } = formState

  const ref = inputRef
    ? mergeRefs<HTMLElement>(
        inputRef as React.MutableRefObject<HTMLElement>,
        // @ts-expect-error | current is marked as readonly
        field.ref as React.MutableRefObject<HTMLElement>,
      )
    : field.ref

  // Eliminate ref from the field object
  const safeField = _.omit(field, 'ref')

  // Merge custom onBlur with react-hook-form's onBlur
  const handleOnBlur = () => {
    field.onBlur() // call react-hook-form's onBlur first
    if (onBlur) onBlur() // then call your custom onBlur
  }

  // Keeps parity with the controlled input value and the react-hook-form field.value
  const handleOnChange = (newValue: string) => {
    field.onChange(newValue)
  }

  return (
    <Input
      {...safeField}
      id={`${id}`}
      captionId={captionId}
      type={type}
      label={label}
      caption={caption}
      maxLength={maxLength}
      error={(isDirty || isSubmitted) && error ? error.message : undefined}
      required={required}
      placeholder={placeholder}
      inputRef={ref}
      autoFocus={autoFocus}
      hideErrorCaption={!!hideErrorCaption}
      inputMode={inputMode}
      onKeyDown={onKeyDown}
      hasCounter={hasCounter}
      hasOverflow={hasOverflow}
      overwriteValueWithFocus={overwriteValueWithFocus}
      autoComplete={autoComplete}
      onFocus={onFocus}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      dataId={dataId}
    />
  )
}

export default InputField
